import React, { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Pill from 'carbon-react/lib/components/pill';

import { ExtendedReportTemplateList } from '@futrli/views/processors/reports/reportTemplatesList';
import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getReportingEditTemplateUrl, matchConsolidationDetailRoutePath } from '@futrli/shared-services';
import { ListingPageRowLayout } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-layout/listing-page-row-layout';
import { ListingPageRowMetricCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-metric-cell/listing-page-row-metric-cell';
import { ListingPageRowNameCell } from '@futrli/ui-components/components/listing-page/listing-page-row/listing-page-row-name-cell/listing-page-row-name-cell';

import { DashboardTemplateActionsPopover } from './dashboard-template-actions-popover';

export const ROW_HEIGHT = 86;

interface DashboardTemplateRowProps {
  template: ExtendedReportTemplateList;
  onDelete?: () => void;
  onPublish?: () => void;
  isResourceSharingEnabled?: boolean;
}

export const DashboardTemplateRow = ({
  template,
  onDelete,
  onPublish,
  isResourceSharingEnabled = false
}: DashboardTemplateRowProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workspaceId = '', organisationId = '' } = useParams();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);

  const isPublished = template.status === 'published';
  const formattedDate = intl.formatDate(template.created, { day: '2-digit', month: 'short', year: 'numeric' });

  const handleNameClick = useCallback(() => {
    navigate(getReportingEditTemplateUrl(workspaceId, organisationId, template.id, isConsolidation));
  }, [navigate, workspaceId, organisationId, template.id, isConsolidation]);

  return (
    <ListingPageRowLayout
      nameColumn={
        <ListingPageRowNameCell
          name={template.name}
          onNameClick={handleNameClick}
        />
      }
      metrics={
        <>
          <ListingPageRowMetricCell
            size="status"
            label={<FormattedMessage id="common.status" defaultMessage="Status" />}
            value={
              isPublished ? (
                <Pill colorVariant="positive" size="S" fill>
                  {intl.formatMessage({ id: 'common.published', defaultMessage: 'Published' })}
                </Pill>
              ) : (
                <Pill size="S" pillRole="status">
                  {intl.formatMessage({ id: 'common.draft', defaultMessage: 'Draft' })}
                </Pill>
              )
            }
          />
          <ListingPageRowMetricCell
            size="default"
            label={<FormattedMessage id="predict.date_created" defaultMessage="Date created" />}
            value={formattedDate}
          />
          {isResourceSharingEnabled && (
            <ListingPageRowMetricCell
              size="wide"
              label={<FormattedMessage id="common.owner" defaultMessage="Owner" />}
              value={
                template.isOwner
                  ? intl.formatMessage({ id: 'common.you', defaultMessage: 'You' })
                  : template.ownerName || '—'
              }
            />
          )}
        </>
      }
      actions={<DashboardTemplateActionsPopover template={template} onDelete={onDelete} onPublish={onPublish} />}
    />
  );
};
